import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiClient } from "../lib/api";
import { useStoreRegion } from "./StoreRegionContext";
import { ArrowRight } from "lucide-react";
import CandleCareSafety from "./CandleCareSafety";

export default function StorefrontHome() {
  const [products, setProducts] = useState(null);
  const [activeCollection, setActiveCollection] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();
  const { current, symbol, priceFor, stockState } = useStoreRegion();

  useEffect(() => {
    apiClient.get("/store/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, []);

  // Sync collection from URL (?collection=pillar) so header menu links work, then scroll to grid.
  useEffect(() => {
    const c = searchParams.get("collection");
    if (c && ["all", "pillar", "designer", "jar"].includes(c)) {
      setActiveCollection(c);
      const el = document.getElementById("shop");
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    } else {
      setActiveCollection("all");
    }
  }, [searchParams]);

  const selectCollection = (key) => {
    setActiveCollection(key);
    setSearchParams({ collection: key });
  };

  const COLLECTIONS = [
    { key: "all", label: "All Candles" },
    { key: "pillar", label: "Pillar Candles" },
    { key: "designer", label: "Designer Candles" },
    { key: "jar", label: "Jar Candles" },
  ];
  const collOf = (p) => p.collection || "designer";
  const filtered =
    products && activeCollection !== "all"
      ? products.filter((p) => collOf(p) === activeCollection)
      : products || [];
  const activeLabel =
    COLLECTIONS.find((c) => c.key === activeCollection)?.label || "All Candles";

  return (
    <>
      {/* Hero */}
      <section className="border-b border-zinc-100">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-32 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.3em] font-semibold luxe-text mb-2">
              The Autumn Collection · 2026
            </div>
            <div
              className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-6"
              data-testid="hero-tagline"
            >
              Hand-Poured in Ontario · Established 2026
            </div>
            <h1
              className="text-6xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.9] mb-8"
              style={{ fontFamily: "Outfit" }}
              data-testid="store-hero-heading"
            >
              Smibha<br/>
              <span className="italic font-extralight luxe-text">Luxe</span><br/>
              Candles
            </h1>
            <p className="text-base md:text-lg text-zinc-600 leading-relaxed max-w-md mb-10">
              Small-batch, hand-poured candles built around a single idea: that scent shapes
              the way a room feels. Premium soy &amp; coconut wax, hand-tied cotton wicks,
              and fragrances composed in our Ontario studio for the rituals that matter.
            </p>
            <a
              href="#shop"
              className="inline-flex items-center gap-2 luxe-bg text-white px-6 py-3 text-xs uppercase tracking-[0.25em] font-bold hover:opacity-90 transition"
              data-testid="hero-cta"
            >
              Shop the Collection
            </a>
          </div>
          <div className="md:col-span-5 relative">
            <div
              className="aspect-[4/5] bg-zinc-100 relative overflow-hidden"
              style={{
                // Brand-owned hero — Eternal Embrace Pillar (in-studio photography).
                backgroundImage:
                  "url('https://customer-assets.emergentagent.com/job_niche-winning-picks/artifacts/6igl4m9j_love%20couple.jpg')",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            <div className="absolute -bottom-6 -left-6 bg-white border border-zinc-200 p-5 shadow-sm hidden md:block">
              <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-400 font-semibold">
                Featured
              </div>
              <div className="text-lg font-black mt-1" style={{ fontFamily: "Outfit" }}>
                {products?.length || 0} products
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product grid */}
      <section id="shop" className="max-w-7xl mx-auto px-6 py-20 md:py-24">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-zinc-500 mb-2">
              Shop by collection
            </div>
            <h2
              className="text-4xl md:text-5xl font-black tracking-tighter"
              style={{ fontFamily: "Outfit" }}
              data-testid="collection-heading"
            >
              {activeLabel}
            </h2>
          </div>
          <div className="text-xs text-zinc-500" data-testid="collection-count">
            {filtered.length} {filtered.length === 1 ? "candle" : "candles"}
          </div>
        </div>

        {/* Collection filter tabs */}
        <div className="flex flex-wrap gap-2 mb-12" data-testid="collection-tabs">
          {COLLECTIONS.map((c) => {
            const count =
              c.key === "all"
                ? (products?.length || 0)
                : (products || []).filter((p) => collOf(p) === c.key).length;
            const active = activeCollection === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => selectCollection(c.key)}
                data-testid={`collection-tab-${c.key}`}
                aria-pressed={active}
                className={`px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-bold border transition ${
                  active
                    ? "luxe-bg text-white border-transparent"
                    : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
                }`}
              >
                {c.label}
                <span className={`ml-2 font-mono ${active ? "opacity-80" : "text-zinc-400"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {products === null && (
          <div className="text-center py-20 text-zinc-400 text-sm">Loading...</div>
        )}

        {products?.length === 0 && (
          <div className="text-center py-20 border border-dashed border-zinc-200" data-testid="empty-store">
            <p className="text-zinc-500">No products yet. Curate winners in the admin dashboard.</p>
            <Link
              to="/admin"
              className="inline-block mt-4 text-xs uppercase tracking-[0.25em] font-bold hover:text-[#002FA7]"
            >
              Go to Admin →
            </Link>
          </div>
        )}

        {products?.length > 0 && (
          <div
            className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12"
            data-testid="store-products-grid"
          >
            {filtered.map((p) => {
              const st = stockState(p);
              const isOOS = st.code === "sold_out";
              return (
              <Link
                key={p.id}
                to={`/products/${p.id}`}
                data-testid={`store-product-${p.id}`}
                className={`group block ${isOOS ? "opacity-70" : ""}`}
              >
                <div className="aspect-square bg-zinc-100 overflow-hidden mb-4 relative">
                  <img
                    src={p.image_url}
                    alt={p.name}
                    className={`w-full h-full object-cover ${
                      isOOS ? "" : "group-hover:scale-105"
                    } transition duration-700`}
                    onError={(e) => {
                      e.currentTarget.src =
                        "https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=800&q=80";
                    }}
                  />
                  {isOOS && (
                    <div
                      className="absolute inset-0 bg-white/80 flex items-center justify-center"
                      data-testid={`sold-out-overlay-${p.id}`}
                    >
                      <span className="bg-zinc-900 text-white px-4 py-2 text-xs tracking-[0.3em] uppercase font-bold">
                        Sold Out
                      </span>
                    </div>
                  )}
                  {st.code === "low" && (
                    <div className="absolute top-3 right-3 bg-amber-500 text-white px-2 py-1 text-[9px] tracking-[0.2em] uppercase font-bold">
                      {st.label}
                    </div>
                  )}
                </div>
                <h3
                  className="text-base md:text-lg font-bold leading-tight mb-1 group-hover:text-[#002FA7] transition"
                  style={{ fontFamily: "Outfit" }}
                >
                  {p.name}
                </h3>
                <p className="text-sm text-zinc-500 mb-2 line-clamp-2">{p.description || p.problem_solved}</p>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-mono font-bold">
                    {symbol}
                    {priceFor(p).toFixed(2)}
                  </div>
                </div>
              </Link>
            );
            })}
          </div>
        )}
      </section>

      {/* About strip */}
      <section id="about" className="bg-zinc-50 border-t border-zinc-100">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-zinc-500 mb-6">
            Our philosophy
          </div>
          <p
            className="text-2xl md:text-3xl leading-relaxed font-light"
            style={{ fontFamily: "Outfit" }}
            data-testid="store-philosophy"
          >
            &ldquo;Scent is the quietest kind of memory. We pour ours one wick at a
            time — slow, deliberate, in small batches you can almost count. Premium
            wax, fragrances built by hand, and the patience to let a single candle
            become a ritual. This is the art of slow living, made to be lit.&rdquo;
          </p>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mt-8">
            — The Smibha Luxe Atelier, Ontario
          </div>
        </div>
      </section>

      {/* Candle Care & Safety */}
      <section className="border-t border-zinc-100 bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-20 sm:pt-24 pb-10">
          <CandleCareSafety />
        </div>
      </section>
    </>
  );
}
